import { useEffect, useState } from "react";
import { useRequiredParams } from "~utils/url";
import { fetchDeviceByExternalId } from "~device/device.queries";
import { useParsedSearchParams } from "@fidelix/fx-miranda";
import { fetchClosestScadaNodeByGraphicsId } from "../tree/tree.queries";
import { queryClient } from "../utils/query-client";
import { fetchGraphicsAttachments } from "./graphics.queries";

const devicePathPattern = /^v\d+-[^/]+\//;

export function useGraphicsNavigation() {
  const [history, setHistory] = useState<{ graphicId: number; file: string }[]>(
    [],
  );
  const { parsedParams, setSearchParams } = useParsedSearchParams({
    graphic: { type: "string", defaultValue: "" },
    graphicsId: { type: "number" },
    deviceId: { type: "number" },
  });
  const { companyId } = useRequiredParams({
    companyId: "number",
  });

  const { graphicsId, graphic } = parsedParams;

  // Check the graphic param for possible device external id
  useEffect(() => {
    if (graphic && devicePathPattern.test(graphic)) {
      const [deviceExternalId, path] = graphic.split("/");
      navigateEventHandler({
        detail: {
          device: deviceExternalId,
          fdxhref: path,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphic]);

  const navigate = (id: number, file: string) => {
    setHistory((prev) => [...prev, { graphicId: graphicsId!, file: graphic! }]);

    setSearchParams((params) => {
      params.set("graphicsId", id.toString());
      params.set("graphic", file);
    });
  };

  const openAttachmentFile = async (
    targetGraphicsId: number,
    filePath: string,
  ): Promise<boolean> => {
    const attachments = await fetchGraphicsAttachments({
      graphicsId: targetGraphicsId,
      companyId,
    });

    const attachment = attachments?.find(
      (a) => a.name.toLowerCase() === filePath.toLowerCase(),
    );

    if (!attachment) {
      return false;
    }

    window.open(attachment.publicUrl, "_blank");
    return true;
  };

  const navigateEventHandler = async (event) => {
    const isGraphic =
      event.detail.fdxhref.toLowerCase().includes(".htm") ||
      event.detail.fdxhref.toLowerCase().includes(".html");

    if (event.detail.fdxhref && event.detail.fdxhref.startsWith("../")) {
      const scadaNode = await queryClient.fetchQuery({
        queryKey: ["graphic-scada-node", companyId, graphicsId],
        queryFn: () =>
          fetchClosestScadaNodeByGraphicsId({
            graphicsId: graphicsId!,
            companyId,
          }),
        // Add a minute worth of staletime so this doesn't get fetched every single click
        staleTime: 60 * 1000,
      });

      const path = event.detail.fdxhref.replace(/^(\.\.\/)+/, "");
      const scadaId = scadaNode?.getId();

      if (!scadaId) {
        return;
      }

      if (!isGraphic) {
        return openAttachmentFile(scadaId, path);
      }

      navigate(scadaId, path);
      return;
    }

    if (!event.detail.device) {
      navigate(graphicsId!, event.detail.fdxhref);
    } else {
      const device = await queryClient.fetchQuery({
        queryKey: ["graphic-device", companyId, event.detail.device],
        queryFn: () =>
          fetchDeviceByExternalId({
            companyId,
            externalId: event.detail.device,
          }),
        // Add a minute worth of staletime so this doesn't get fetched every single click
        staleTime: 60 * 1000,
      });

      if (!device) {
        throw new Error(`Device not found: ${event.detail.device}`);
      }

      // for now there is always just one graphic for each device
      if (!device.graphicsId) {
        throw new Error(
          `Device ${device.externalId} has no graphics or has more than one graphic`,
        );
      }
      navigate(device.graphicsId, event.detail.fdxhref);
    }
  };

  return {
    navigateEventHandler,
    history,
    setHistory,
  };
}
